import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatEuro, formatNumber } from "@/lib/format";
import { Building, Search, Plus, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  BUILDING_TYPE_LABELS, BUILDING_STATUS_LABELS, BUILDING_STATUS_TONES,
} from "@/constants/patrimoine";
import SmartLoader from "@/components/common/SmartLoader";

const EMPTY = {
  company_id: "", name: "", building_type: "immeuble",
  address: "", postal_code: "", city: "",
  acquisition_date: "", acquisition_price: "",
  current_value: "", total_surface_m2: "", year_built: "",
  status: "in_operation",
};

export default function BuildingsListPage() {
  const [buildings, setBuildings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filters, setFilters] = useState({ search: "", company_id: "any", building_type: "any", status: "any" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  const query = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "any") p.set(k, v); });
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [b, c] = await Promise.all([api.get(`/buildings${query}`), api.get("/companies")]);
      setBuildings(b.data.items || []); setCompanies(c.data.items || []);
    } catch (e) {
      setLoadError(e.response?.data?.detail || "Impossible de charger les bâtiments.");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [query]);

  const create = async () => {
    try {
      const payload = { ...form };
      ["acquisition_price", "current_value", "total_surface_m2", "year_built"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = k === "year_built" ? parseInt(payload[k]) : parseFloat(payload[k]);
      });
      if (!payload.acquisition_date) delete payload.acquisition_date;
      await api.post("/buildings", payload);
      toast.success("Immeuble créé"); setOpen(false); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Création impossible"); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.denomination || "—";

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Patrimoine"
        title="Immeubles et biens"
        description="Bâtiments, immeubles, entrepôts, garages, terrains. Chaque bâtiment contient un ou plusieurs lots."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-pill" data-testid="building-add-btn"><Plus className="mr-2 h-4 w-4" /> Nouveau bâtiment</Button>
            </DialogTrigger>
            <CreateBuildingDialog form={form} setForm={setForm} onSave={create} onCancel={() => setOpen(false)} companies={companies} />
          </Dialog>
        }
      />

      {/* Filters */}
      <div className="card-elevated p-4 lg:p-5 mb-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Nom, adresse, cadastre…" className="pl-9"
                     value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                     data-testid="buildings-filter-search" />
            </div>
          </div>
          <FilterSelect label="Société" value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v })}
                        options={[["any", "Toutes les sociétés"], ...companies.map((c) => [c.id, c.denomination])]} testId="buildings-filter-company" />
          <FilterSelect label="Type" value={filters.building_type} onValueChange={(v) => setFilters({ ...filters, building_type: v })}
                        options={[["any", "Tous les types"], ...Object.entries(BUILDING_TYPE_LABELS)]} testId="buildings-filter-type" />
          <FilterSelect label="Statut" value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}
                        options={[["any", "Tous statuts"], ...Object.entries(BUILDING_STATUS_LABELS)]} testId="buildings-filter-status" />
        </div>
      </div>

      {/* Buildings grid */}
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {(loading || loadError) && (
          <div className="col-span-full">
            <SmartLoader label="Chargement des bâtiments…" onRetry={load} error={loadError} />
          </div>
        )}
        {!loading && !loadError && buildings.length === 0 && (
          <div className="col-span-full card-elevated p-12 text-center text-muted-foreground">
            <Building className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" />
            Aucun bâtiment ne correspond aux filtres.
          </div>
        )}
        {buildings.map((b) => (
          <Link key={b.id} to={`/app/buildings/${b.id}`} data-testid={`building-card-${b.id}`}
                className="card-elevated p-6 hover:shadow-elevated transition-all block group">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-accent">
                    {BUILDING_TYPE_LABELS[b.building_type] || b.building_type}
                  </span>
                  <StatusBadge tone={BUILDING_STATUS_TONES[b.status] || "neutral"}>
                    {BUILDING_STATUS_LABELS[b.status] || b.status}
                  </StatusBadge>
                </div>
                <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{b.name}</h3>
                <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {b.address}, {b.postal_code} {b.city}
                </div>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-accent" />
            </div>
            <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center">
              <StatCell label="Valeur" value={formatEuro(b.current_value || b.acquisition_price)} />
              <StatCell label="Surface" value={b.total_surface_m2 ? `${formatNumber(b.total_surface_m2)} m²` : "—"} />
              <StatCell label="Société" value={companyName(b.company_id)} small />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatCell({ label, value, small }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-heading ${small ? "text-xs truncate" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onValueChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function CreateBuildingDialog({ form, setForm, onSave, onCancel, companies }) {
  return (
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>Nouveau bâtiment</DialogTitle></DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label>Société propriétaire</Label>
          <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
            <SelectTrigger data-testid="building-form-company"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denomination}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Nom interne</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="building-form-name" required />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.building_type} onValueChange={(v) => setForm({ ...form, building_type: v })}>
              <SelectTrigger data-testid="building-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(BUILDING_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Adresse</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <div><Label>CP</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Date acquisition</Label><Input type="date" value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} /></div>
          <div><Label>Prix d&apos;achat (€)</Label><Input type="number" value={form.acquisition_price} onChange={(e) => setForm({ ...form, acquisition_price: e.target.value })} /></div>
          <div><Label>Valeur estimée (€)</Label><Input type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Surface totale (m²)</Label><Input type="number" value={form.total_surface_m2} onChange={(e) => setForm({ ...form, total_surface_m2: e.target.value })} /></div>
          <div><Label>Année de construction</Label><Input type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Annuler</Button>
        <Button onClick={onSave} data-testid="building-form-submit">Créer le bâtiment</Button>
      </DialogFooter>
    </DialogContent>
  );
}
